import React, { useRef } from "react";
import { Heading, LoadingContainer, ScreenResponsive, TotalAnticipation } from "../../components";
import { observer } from "mobx-react-lite";
import { strings } from "../../localization";
import { IconQuestionRegular, skinVars, Tooltip, useScreenSize } from "@telefonica/mistica";
import { useGlobalStore } from "../../contexts/globalStoreContext";
import { useAnalytics } from "../../hooks/useAnalytics";
import { TabSelectedEnum } from "../../stores";
import { useFetchSimulation, useNavigateCustom } from "../../hooks";
import { Box, Divider, Flex, Text } from "@chakra-ui/react";
import type { TotalSimulationResponse } from "../../services/APIService/types";

const Anticipation: React.FC = observer(() => {
	const navigate = useNavigateCustom();
	const { getSimulation, load } = useFetchSimulation();
	const firstSimulationRef = useRef(false);
	const anticipationStrings = strings.pages.anticipation;
	const { anticipationStore } = useGlobalStore();
	const store = anticipationStore;
	const { addAnalyticEvent, updatePageView, addAnalyticEventPurchase } = useAnalytics();
	const {maxFGTSValueAvailable, partialSimulationData } = store.partialAnticipationShelf;

	const valueToRecivePartial = partialSimulationData.value?.disbursedIssueAmount || 0;
	const maxValue = maxFGTSValueAvailable.value;
	const valueToReciveTotal = store.totalAnticipationShelf.disbursedIssueAmount.value;
	const { isDesktopOrBigger } = useScreenSize();
	const {
		withdrawTitle,
		anticipationTitle,
		anticipationWithdraw,
		helperText,
	} = strings.components.anticipationSimulation;

	const handleAnalyticInteraction = ( description: string, step: string, value: number | string, component?: "body" | "header" ): void => {
		addAnalyticEvent({
			component: component ? component : "body",
			action: "click.button",
			description,
			step,
			value,
		}, {applySanitize: true});
	};

	const handleAnalyticPurchase = (): void => {
		const { disbursedIssueAmount } = store.totalAnticipationShelf;

		addAnalyticEventPurchase({
			event: "add_to_cart",
			ecommerce: {
				value: valueToReciveTotal,
			},
			item: {
				item_name: "antecipacao-total-fgts",
				price: disbursedIssueAmount.value,
				item_id: "12345",
			},
		});
	};

	const onBack = (): void => {
		let step;
		let value;

		switch(store.getTabSelected()) {
			case TabSelectedEnum.DEFAULT:
				value = maxValue;
				step = "fgts:simulador-quente:resultado";
				break;
			case TabSelectedEnum.PARTIAL:
				value = valueToRecivePartial;
				step = "fgts:simulador-quente:resultado:parcial";
				break;
			case TabSelectedEnum.TOTAL:
				value = valueToReciveTotal;
				step = "fgts:simulador-quente:resultado:total";
				break;
		}

		handleAnalyticInteraction(strings.components.buttons.anticipateFGTS, step, value!, "header");
		navigate(-1);
	};

	const fetchGetTotalSimulation = async () => {
		if (store.offersSimulated.value.length > 0) {
			return;
		}

		await getSimulation();
		store.onSuccessFetch(store.offersSimulated.value[0]);
	};

	const onSuccessConfirmAnticipationRedirect = (): void => {
		navigate("/simulator-summary");
	};

	const onSelectOffer = (offer: TotalSimulationResponse) => {
		store.onSuccessFetch(offer);
	};

	React.useEffect(() => {
		if (!firstSimulationRef.current) {
			firstSimulationRef.current = true;
			fetchGetTotalSimulation();
		}

		updatePageView({
			name: "Vivo Money FGTS Simulador Quente Resultado",
			pagePath: "/fgts/simulador-quente/resultado",
		});
	}, []);

	return (
		<ScreenResponsive
			isLogo
			onBack={onBack}
			navigationTitle={strings.components.buttons.anticipateFGTS}
			responsiveLayoutProps={{ backgroundColor: skinVars.colors.backgroundAlternative }}
		>
			{load ? (
				<Box flex={1} display="flex" alignItems="center" justifyContent="center" height="100vh">
					<LoadingContainer title={"Carregando ofertas, aguarde alguns instantes"} />
				</Box>
			)
				: (
					<>
						<Heading title={anticipationStrings.header} />
						<Box className="data-card-centered centered-on-desktop" paddingBottom={56}>
							<Box bg="white" p={6} alignItems={"left"} borderRadius={16} boxShadow={"0px 0px 7px rgba(42, 42, 60, 0.2)"}>
								<Flex alignItems="center" gap={2}>
									<Text fontSize="20px" fontWeight="500" color="black">{withdrawTitle}</Text>
									<Tooltip
										targetLabel="help"
										target={<IconQuestionRegular color={skinVars.colors.backgroundBrand} size={24} />}
										position={"top"}
										description={helperText}
										width={isDesktopOrBigger ? 343 : 220}
									/>
								</Flex>

								<Divider my={4}/>

								<Box alignItems="left">
									<Text fontSize="18px" fontWeight="500" color="black" textAlign="left">{anticipationTitle}</Text>
									<Text textAlign="left" color="#666666" pt={2}>{anticipationWithdraw}</Text>
								</Box>


								<TotalAnticipation
									key={TabSelectedEnum.TOTAL}
									totalAnticipationShelf={store.totalAnticipationShelf}
									onConfirm={() => {
										handleAnalyticInteraction(strings.components.buttons.anticipateFGTS, "fgts:simulador-quente:resultado:total", valueToReciveTotal);
										handleAnalyticPurchase();
										onSuccessConfirmAnticipationRedirect();
									}}
									offers={store.offersSimulated.value}
									onSelectOffer={(selectedOffer) => onSelectOffer(selectedOffer)}
									currentComponentControl={() => store.onSelectTab(TabSelectedEnum.TOTAL)}
								/>
							</Box>
						</Box>
					</>
				)}
		</ScreenResponsive>
	);
});

export default Anticipation;
